import React, { useState, useCallback } from 'react';
import { documents } from '../mock';
import { UploadCloud, FileText, Search, Filter, CheckCircle2, Clock, Send } from 'lucide-react';
import { cn } from '../lib/utils';

export default function Documents() {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('All');
  const [dragOver, setDragOver] = useState(false);

  const categories = ['All', ...Array.from(new Set(documents.map((d) => d.category)))];

  const filtered = documents.filter((d) => {
    const matchesCat = category === 'All' || d.category === category;
    const matchesQ = d.name.toLowerCase().includes(query.toLowerCase());
    return matchesCat && matchesQ;
  });

  const onDrop = useCallback((e) => { e.preventDefault(); setDragOver(false); }, []);

  return (
    <div className="max-w-[1300px] mx-auto space-y-5">
      <div>
        <div className="text-[11.5px] text-neutral-500 mono">Loan #126096012900</div>
        <h1 className="display text-[32px] font-bold tracking-tight text-neutral-900 mt-1">Documents</h1>
        <p className="text-[13px] text-neutral-500">Upload, categorize, and track every loan file artifact.</p>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={cn(
          'rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-colors bg-white relative overflow-hidden',
          dragOver ? 'border-[hsl(var(--kf-gold))] bg-[hsl(var(--kf-gold-soft))]' : 'border-neutral-300 hover:border-neutral-400'
        )}
      >
        <div className="absolute inset-0 bg-dot opacity-40" />
        <div className="relative">
          <div className="mx-auto h-14 w-14 rounded-2xl bg-neutral-900 text-[hsl(var(--kf-gold))] flex items-center justify-center">
            <UploadCloud className="h-6 w-6" />
          </div>
          <div className="mt-3 display text-[17px] font-bold text-neutral-900">Drop files here or click to upload</div>
          <div className="text-[11.5px] text-neutral-500 mt-1">PDF, PNG, JPG up to 25 MB · Auto-categorized by Kinfolk AI</div>
          <div className="mt-4 inline-flex items-center gap-2">
            <button className="h-9 px-4 rounded-md btn-ink text-[12.5px] font-semibold">Choose files</button>
            <button className="h-9 px-4 rounded-md bg-white border hairline text-[12.5px] font-semibold text-neutral-700 flex items-center gap-1.5">
              <Send className="h-4 w-4" /> Send upload link
            </button>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search documents…"
            className="w-full h-9 pl-9 pr-3 rounded-lg bg-white border hairline text-[13px] placeholder:text-neutral-400 focus:outline-none focus:border-neutral-900"
          />
        </div>
        <div className="flex items-center gap-1 rounded-lg border hairline p-0.5 bg-white overflow-x-auto">
          <Filter className="h-3.5 w-3.5 text-neutral-400 ml-2 shrink-0" />
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={cn(
                'text-[10.5px] font-bold px-2.5 py-1 rounded-md transition whitespace-nowrap',
                category === c ? 'bg-neutral-900 text-white' : 'text-neutral-500 hover:text-neutral-900'
              )}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      <div className="rounded-2xl border hairline bg-white overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 text-[10px] uppercase tracking-wider text-neutral-500 font-bold border-b hairline bg-neutral-50/60">
          <div className="col-span-5">Document</div>
          <div className="col-span-2">Category</div>
          <div className="col-span-2">Uploaded</div>
          <div className="col-span-2">By</div>
          <div className="col-span-1 text-right">Status</div>
        </div>
        <div className="divide-y divide-neutral-100">
          {filtered.map((d) => (
            <div key={d.name} className="grid grid-cols-1 md:grid-cols-12 gap-3 px-5 py-3 items-center hover:bg-neutral-50/60">
              <div className="col-span-5 flex items-center gap-3 min-w-0">
                <div className="h-9 w-9 rounded-lg bg-neutral-50 border hairline flex items-center justify-center shrink-0">
                  <FileText className="h-4 w-4 text-neutral-700" />
                </div>
                <div className="min-w-0">
                  <div className="text-[12.5px] font-bold text-neutral-900 truncate">{d.name}</div>
                  <div className="text-[10.5px] text-neutral-500 mono">{d.size}</div>
                </div>
              </div>
              <div className="col-span-2 text-[11.5px] text-neutral-700">{d.category}</div>
              <div className="col-span-2 text-[11.5px] text-neutral-700">{d.uploaded}</div>
              <div className="col-span-2 text-[11.5px] text-neutral-700">{d.uploader}</div>
              <div className="col-span-1 flex md:justify-end">
                <StatusChip status={d.status} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatusChip({ status }) {
  const map = {
    Accepted: { cls: 'bg-emerald-50 text-emerald-700 border-emerald-100', icon: CheckCircle2 },
    'In Review': { cls: 'bg-[hsl(var(--kf-gold-soft))] text-[hsl(var(--kf-gold-deep))] border-[hsl(var(--kf-gold))]/30', icon: Clock },
    Sent: { cls: 'bg-neutral-100 text-neutral-700 border-neutral-200', icon: Send },
    Pending: { cls: 'bg-amber-50 text-amber-700 border-amber-100', icon: Clock },
  };
  const { cls, icon: Icon } = map[status] || map.Pending;
  return (
    <span className={`inline-flex items-center gap-1 text-[9.5px] font-bold uppercase tracking-wider px-2 py-1 rounded-md border ${cls}`}>
      <Icon className="h-3 w-3" /> {status}
    </span>
  );
}
